/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import * as React from 'react';

import {
    InventoryTag,
    formatMoney,
    isIOS,
    isAndroid,
    FormattedDateTime,
} from 'sarsaparilla';

import type { Reservation } from './OrderConfirmationPage.types';

interface OrderConfirmationPageInventoryItemProps {
    reservation: Reservation;
}

export function OrderConfirmationPageInventoryItem({
    reservation,
}: OrderConfirmationPageInventoryItemProps) {
    const validDays = reservation?.order_details?.valid_days;
    const allowMultiDays = reservation?.order_details?.allow_multidays;
    const tourDate = getTourDate(reservation.order_details.tour_date);
    const endDate = !!validDays && validDays > 1 ? getEndDate(tourDate, 3) : '';

    return (
        <div className="rec-order-confirmation-reservation">
            <div className="rec-order-confirmation-reservation-column">
                <div className="rec-order-confirmation-reservation-dates">
                    {!allowMultiDays && (
                        <>
                            <p className="strong">
                                Tour Date:{' '}
                                {tourDate && (
                                    <FormattedDateTime
                                        date={new Date(tourDate)}
                                        format="ll"
                                        timeZone="UTC"
                                    />
                                )}
                            </p>
                            <p>Start Time: {reservation?.order_details?.tour_start}</p>
                            <p>End Time: {reservation?.order_details?.tour_end}</p>
                        </>
                    )}
                    {!!allowMultiDays && (
                        <>
                            <p className="strong">
                                {tourDate && (
                                    <FormattedDateTime
                                        date={new Date(tourDate)}
                                        format="ll"
                                        timeZone="UTC"
                                    />
                                )}
                            </p>
                            <p>Start Date</p>
                            <hr />
                            <p className="strong">
                                {endDate && (
                                    <FormattedDateTime
                                        date={new Date(endDate)}
                                        format="ll"
                                        timeZone="UTC"
                                    />
                                )}
                            </p>
                            <p>Valid Through</p>
                            <p className="strong mt-2 mb-half">Entry Window</p>
                            <p>{`${reservation?.order_details?.tour_start} - ${reservation?.order_details?.tour_end}`}</p>
                        </>
                    )}
                </div>
            </div>
            <div className="rec-order-confirmation-reservation-column">
                <div className="rec-order-confirmation-reservation-icon">
                    <InventoryTag.InventoryTicket isSpanItem />
                </div>
                {reservation.order_details.qrCode ? (
                    <div>
                        <img
                            src={reservation.order_details.qrCode}
                            alt="QR Code"
                            width="100"
                        />
                    </div>
                ) : null}
                <div className="rec-order-confirmation-reservation-description">
                    <p className="strong">{reservation.order_details.description}</p>
                    <p>
                        <i>{reservation.order_details.recarea}</i>
                    </p>
                    <p>{reservation.order_details.location}</p>
                </div>
            </div>
            {reservation.order_details.appleWalletLink && isIOS() ? (
                <div className="rec-order-confirmation-reservation-column rec-order-confirmation-reservation-mobile-link">
                    <a
                        rel="noopener noreferrer"
                        href={reservation.order_details.appleWalletLink}
                        target="_blank"
                    >
                        <img
                            src="https://cdn.recreation.gov/app/email-template/email-images/apple-wallet.png"
                            alt="Apple Wallet"
                            width="300"
                        />
                    </a>
                </div>
            ) : null}
            {reservation.order_details.googlePassLink && isAndroid() ? (
                <div className="rec-order-confirmation-reservation-column rec-order-confirmation-reservation-mobile-link">
                    <a
                        rel="noopener noreferrer"
                        href={reservation.order_details.googlePassLink}
                        target="_blank"
                    >
                        <img
                            src="https://cdn.recreation.gov/app/email-template/email-images/google-pay.png"
                            alt="Google Pay"
                            width="300"
                        />
                    </a>
                </div>
            ) : null}
            <div className="rec-order-confirmation-reservation-column">
                <div className="rec-order-confirmation-reservation-total">
                    <p>
                        Subtotal: <strong>{formatMoney(reservation.subtotal)}</strong>
                    </p>
                    <p>
                        Quantity: <strong>{reservation.quantity} Ticket(s)</strong>
                    </p>
                </div>
            </div>
        </div>
    );
}

function getTourDate(date: string) {
    if (date) {
        return new Date(date).toISOString();
    }

    return '';
}

function getEndDate(date: string, daysToAdd: number) {
    if (date) {
        const tourDate = new Date(date);
        const endDate = new Date(tourDate.setDate(tourDate.getDate() + (daysToAdd - 1)));

        return endDate.toISOString();
    }

    return '';
}

// cSpell:ignore multidays, recarea
